"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  Hammer,
  FolderTree,
  Package,
  FileCode,
  Cpu,
  CheckCircle2,
  Loader2,
  Circle,
  XCircle,
  ExternalLink,
  X,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/components/auth/auth-provider";
import { createCEPAClientWithToken } from "@/lib/cepa-client";
import { useForgeStore } from "@/lib/stores/forge-store";
import { cn } from "@/lib/utils";

// ─── Forge Stages ───────────────────────────────────────────────────────────

const STAGES = [
  { id: "create", label: "Creating CEPA", icon: Hammer },
  { id: "filter", label: "Filtering paths", icon: FolderTree },
  { id: "models", label: "Pydantic models", icon: Package },
  { id: "client", label: "Python client", icon: FileCode },
  { id: "mcp", label: "MCP tools", icon: Cpu },
] as const;

type StageStatus = "pending" | "in_progress" | "completed";

// ─── Component ──────────────────────────────────────────────────────────────

export function ForgeTracker() {
  const router = useRouter();
  const { session } = useAuth();
  const token = session?.token;

  const { activeForge, completeForge, failForge, dismissForge } =
    useForgeStore();

  const [stages, setStages] = useState<StageStatus[]>(
    STAGES.map(() => "pending"),
  );
  const [progress, setProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stageIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Entrance animation ─────────────────────────────────────────────

  useEffect(() => {
    if (activeForge) {
      // Small delay to trigger CSS entrance transition
      const t = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [activeForge]);

  // ─── Simulated stage progression while forging ──────────────────────

  useEffect(() => {
    if (activeForge?.status !== "forging") {
      if (stageIntervalRef.current) clearInterval(stageIntervalRef.current);
      return;
    }

    // Reset stages on new forge
    setStages(STAGES.map((_, i) => (i === 0 ? "in_progress" : "pending")));
    setProgress(5);

    const progressTick = setInterval(() => {
      setProgress((p) => (p >= 92 ? p : p + Math.random() * 3));
    }, 600);

    stageIntervalRef.current = setInterval(() => {
      setStages((prev) => {
        const activeIdx = prev.indexOf("in_progress");
        if (activeIdx === -1 || activeIdx >= prev.length - 1) return prev;
        const next = [...prev];
        next[activeIdx] = "completed";
        next[activeIdx + 1] = "in_progress";
        return next;
      });
    }, 2200);

    return () => {
      clearInterval(progressTick);
      if (stageIntervalRef.current) clearInterval(stageIntervalRef.current);
    };
  }, [activeForge?.status, activeForge?.masterId]);

  // ─── Poll backend for real status ───────────────────────────────────

  useEffect(() => {
    if (!activeForge || activeForge.status !== "forging" || !token) return;

    const poll = async () => {
      try {
        const client = createCEPAClientWithToken(token);
        const master = await client.getMasterCepa(activeForge.masterId);

        if (master.status === "active" && master.last_generated_at) {
          setStages(STAGES.map(() => "completed"));
          setProgress(100);
          const specPaths = (
            master.openapi_spec as Record<string, unknown> | undefined
          )?.paths;
          const pathCount =
            specPaths && typeof specPaths === "object"
              ? Object.keys(specPaths as Record<string, unknown>).length
              : 0;
          completeForge({
            mcpToolsCount: master.mcp_tools?.length ?? 0,
            endpointsCount: pathCount || master.mcp_tools?.length || 0,
          });
        } else if (master.generation_error) {
          failForge(master.generation_error);
        }
      } catch {
        // Silent retry — network blip
      }
    };

    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [activeForge, token, completeForge, failForge]);

  // ─── Auto-dismiss on completion (pauses on hover) ───────────────────

  useEffect(() => {
    if (activeForge?.status !== "completed") {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      return;
    }

    if (!isHovered) {
      dismissTimerRef.current = setTimeout(() => handleDismiss(), 30_000);
    }

    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [activeForge?.status, isHovered]);

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      dismissForge();
      setExiting(false);
      setVisible(false);
      setProgress(0);
      setStages(STAGES.map(() => "pending"));
    }, 300);
  }, [dismissForge]);

  const handleRetry = useCallback(async () => {
    if (!activeForge || !token) return;
    try {
      const client = createCEPAClientWithToken(token);
      await client.generateArtifacts(activeForge.masterId);
      // Reset to forging state in store
      useForgeStore.getState().startForge({
        masterId: activeForge.masterId,
        cepaName: activeForge.cepaName,
        connectionName: activeForge.connectionName,
      });
    } catch {
      // Already in error state
    }
  }, [activeForge, token]);

  const handleViewCepa = useCallback(() => {
    if (!activeForge) return;
    router.push(`/connection-studio?cepa=${activeForge.masterId}`);
    handleDismiss();
  }, [activeForge, router, handleDismiss]);

  // ─── Render guard ───────────────────────────────────────────────────

  if (!activeForge) return null;

  const isForging = activeForge.status === "forging";
  const isComplete = activeForge.status === "completed";
  const isError = activeForge.status === "error";

  const elapsed = Math.round((Date.now() - activeForge.startedAt) / 1000);
  const elapsedLabel =
    elapsed < 60
      ? `${elapsed}s`
      : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;

  // ─── Render ─────────────────────────────────────────────────────────

  return (
    <div
      className={cn(
        "fixed bottom-5 right-5 z-[9999] w-[320px]",
        "transition-all duration-500",
        visible && !exiting
          ? "translate-y-0 opacity-100"
          : "translate-y-4 opacity-0 pointer-events-none",
      )}
      style={{
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={cn(
          "relative overflow-hidden rounded-xl border bg-[#0c1220]/95 backdrop-blur-xl",
          "transition-shadow duration-700",
          isForging &&
            "border-blue-500/15 shadow-[0_8px_40px_rgba(0,0,0,0.5),0_0_20px_rgba(139,92,246,0.1)]",
          isComplete &&
            "border-emerald-500/15 shadow-[0_8px_40px_rgba(0,0,0,0.5),0_0_20px_rgba(16,185,129,0.1)]",
          isError &&
            "border-rose-500/15 shadow-[0_8px_40px_rgba(0,0,0,0.5),0_0_20px_rgba(244,63,94,0.1)]",
        )}
      >
        {/* Ambient glow */}
        <div
          className={cn(
            "pointer-events-none absolute inset-0 transition-opacity duration-700",
            isForging &&
              "bg-[radial-gradient(ellipse_at_top_right,_rgba(139,92,246,0.06),_transparent_60%)]",
            isComplete &&
              "bg-[radial-gradient(ellipse_at_top_right,_rgba(16,185,129,0.06),_transparent_60%)]",
            isError &&
              "bg-[radial-gradient(ellipse_at_top_right,_rgba(244,63,94,0.06),_transparent_60%)]",
          )}
        />

        {/* ─── Header ─── */}
        <div className="relative flex items-start gap-3 px-4 pt-4 pb-3">
          {/* Icon */}
          <div
            className={cn(
              "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-500",
              isForging && "bg-blue-500/10 text-teal-300",
              isComplete && "bg-emerald-500/10 text-emerald-400",
              isError && "bg-rose-500/10 text-rose-400",
            )}
          >
            {isForging && (
              <Hammer className="h-4 w-4 animate-[pulse_2s_ease-in-out_infinite]" />
            )}
            {isComplete && <CheckCircle2 className="h-4 w-4" />}
            {isError && <XCircle className="h-4 w-4" />}
          </div>

          {/* Text */}
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-white/90">
              {activeForge.cepaName}
            </p>
            <p className="mt-0.5 text-[13px] text-white/60">
              {isForging && "Forging MCP tools..."}
              {isComplete && `${activeForge.mcpToolsCount} MCP tools forged`}
              {isError && "Forge failed"}
            </p>
          </div>

          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-white/35 transition-colors hover:bg-white/[0.06] hover:text-white/40"
          >
            <X className="h-3 w-3" />
          </button>
        </div>

        {/* ─── Progress bar (forging only) ─── */}
        {isForging && (
          <div className="px-4">
            <div className="h-[3px] w-full overflow-hidden rounded-full bg-white/[0.04]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500/70 to-violet-400/50 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-1.5 flex items-center justify-between">
              <span className="text-[9px] tabular-nums text-white/35">
                {Math.round(progress)}%
              </span>
              <span className="text-[9px] tabular-nums text-white/35">
                {elapsedLabel}
              </span>
            </div>
          </div>
        )}

        {/* ─── Stages (forging only) ─── */}
        {isForging && (
          <div className="mt-1 space-y-px px-4 pb-4">
            {STAGES.map((stage, i) => {
              const status = stages[i];
              const Icon = stage.icon;
              return (
                <div
                  key={stage.id}
                  className={cn(
                    "flex items-center gap-2.5 rounded-md px-2.5 py-[7px] transition-all duration-300",
                    status === "in_progress" && "bg-blue-500/[0.05]",
                    status === "completed" && "bg-emerald-500/[0.02]",
                  )}
                >
                  <div className="flex-shrink-0">
                    {status === "completed" ? (
                      <CheckCircle2 className="h-3 w-3 text-emerald-400/70" />
                    ) : status === "in_progress" ? (
                      <Loader2 className="h-3 w-3 animate-spin text-teal-300/80" />
                    ) : (
                      <Circle className="h-3 w-3 text-white/[0.07]" />
                    )}
                  </div>
                  <span
                    className={cn(
                      "text-[10px] font-medium transition-colors",
                      status === "pending" && "text-white/35",
                      status === "in_progress" && "text-blue-300/70",
                      status === "completed" && "text-emerald-300/50",
                    )}
                  >
                    {stage.label}
                  </span>
                  <Icon
                    className={cn(
                      "ml-auto h-2.5 w-2.5 transition-colors",
                      status === "pending" && "text-white/[0.05]",
                      status === "in_progress" && "text-teal-300/30",
                      status === "completed" && "text-emerald-400/20",
                    )}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* ─── Completion stats ─── */}
        {isComplete && (
          <div className="px-4 pb-4">
            <div className="flex gap-3">
              <div className="flex-1 rounded-lg border border-emerald-500/10 bg-emerald-500/[0.03] px-3 py-2.5">
                <p className="text-[18px] font-semibold tabular-nums text-emerald-300/80">
                  {activeForge.mcpToolsCount}
                </p>
                <p className="text-[9px] font-medium uppercase tracking-[0.15em] text-emerald-400/30">
                  MCP Tools
                </p>
              </div>
              <div className="flex-1 rounded-lg border border-white/[0.04] bg-white/[0.015] px-3 py-2.5">
                <p className="text-[18px] font-semibold tabular-nums text-white/60">
                  {activeForge.endpointsCount}
                </p>
                <p className="text-[9px] font-medium uppercase tracking-[0.15em] text-white/40">
                  Endpoints
                </p>
              </div>
            </div>

            <button
              onClick={handleViewCepa}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-500/20 bg-gradient-to-b from-emerald-500/15 to-emerald-600/8 px-4 py-2.5 text-[13px] font-semibold text-emerald-200 shadow-[0_1px_8px_rgba(16,185,129,0.12)] transition-all hover:border-emerald-400/30 hover:from-emerald-500/20 hover:to-emerald-600/12 hover:shadow-[0_2px_16px_rgba(16,185,129,0.18)]"
            >
              <ExternalLink className="h-3 w-3" />
              View forged CEPA
            </button>
          </div>
        )}

        {/* ─── Error state ─── */}
        {isError && (
          <div className="px-4 pb-4">
            <div className="rounded-lg border border-rose-500/10 bg-rose-500/[0.03] p-3">
              <p className="line-clamp-2 text-[13px] text-rose-300/60">
                {activeForge.error || "An unexpected error occurred"}
              </p>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleRetry}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-rose-500/15 bg-rose-500/[0.06] px-3 py-2 text-[13px] font-semibold text-rose-200/80 transition-all hover:border-rose-400/25 hover:bg-rose-500/10"
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </button>
              <button
                onClick={handleDismiss}
                className="flex flex-1 items-center justify-center rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-[13px] font-medium text-white/60 transition-all hover:bg-white/[0.05] hover:text-white/50"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
